/**
 * The ball ledger's event types. This is the only authoritative representation of
 * a match — everything else in the system is derived from a list of these
 * (ADR-0002).
 */
import type {
  ClipId,
  DeviceId,
  EventId,
  InningsNo,
  MatchId,
  PlayerId,
  TeamId,
  UserId,
} from "./ids.ts";

// ── Provenance ──────────────────────────────────────────────────────────────

/**
 * Who or what produced this event.
 *
 * P0 only ever writes `"human"` with confidence 1. The field exists from day one
 * because it is the reason an AI phase needs no re-architecture: a CV inference
 * service later emits this same object with `"ai_auto"` and a confidence, and
 * every downstream consumer — scorecard, stats, standings, careers — is already
 * written against it. Adding it later would mean migrating the ledger table.
 */
export type EventSource = "human" | "ai_assist" | "ai_auto" | "sensor";

/** Binds a ball to video. Reserved: highlights (P2) and the CV training corpus. */
export interface VideoRef {
  readonly clipId: ClipId;
  readonly startMs: number;
  readonly endMs: number;
}

export interface Provenance {
  readonly source: EventSource;
  /** 1 for human entry. Below 1 only for non-human producers. */
  readonly confidence: number;
  readonly deviceId: DeviceId | null;
  readonly scorerId: UserId | null;
  /** Set when a human confirmed a proposal from a non-human producer. */
  readonly confirmedBy: UserId | null;
  readonly videoRef: VideoRef | null;
}

// ── Every event ─────────────────────────────────────────────────────────────

interface EventBase {
  /** Client-generated UUIDv7, so retries are idempotent. */
  readonly eventId: EventId;
  readonly matchId: MatchId;
  readonly inningsNo: InningsNo;
  /**
   * A permanent *address* within the innings — monotonic, gap-free, and **not** an
   * assertion about wall-clock order. A correction written later carries a higher
   * `seq` while taking effect at the position of the event it supersedes, so no
   * code may assume higher `seq` means "happened later in the match" (ADR-0004).
   */
  readonly seq: number;
  /** The `eventId` this event replaces, or null. Nothing is ever deleted. */
  readonly supersedes: EventId | null;
  /** Marked as never having happened — a dead ball recorded in error. Stays in
   *  the ledger and is skipped during replay. */
  readonly voided: boolean;
  readonly provenance: Provenance;
  /** When the scorer tapped it, from the device clock. */
  readonly recordedAt: string;
}

// ── Delivery ────────────────────────────────────────────────────────────────

export interface Extras {
  readonly wide: number;
  readonly noball: number;
  readonly bye: number;
  readonly legbye: number;
  /**
   * Penalty runs **to the batting side only**, because extras on a delivery are
   * added to the batting total by construction.
   *
   * A penalty awarded to the *fielding* side — a deliberate short run, say — must
   * be a `PenaltyRuns` event, which carries `awardedTo`. Putting it here would add
   * five runs to the wrong side, a ten-run swing, and render it in the batting
   * side's extras line.
   */
  readonly penalty: number;
}

export interface Runs {
  /** Credited to the striker. Never includes extras. */
  readonly batter: number;
  readonly extras: Extras;
  /**
   * Advisory. `replay()` derives the total itself and ignores this field.
   *
   * It is carried so a payload is readable in the database and in logs, but the
   * ledger stores no cricket number as truth (ADR-0002). If a superseding
   * correction changed `extras.legbye` and left this stale, a fold that trusted it
   * would disagree with one that recomputed — which is the exact bug class
   * ADR-0001 exists to prevent, and the server never rejects the event (ADR-0011).
   * The same applies to `legal`, `over` and `ballInOver` below.
   */
  readonly total: number;
  readonly boundary: "four" | "six" | null;
  /**
   * A boundary reached via overthrow credits runs-run plus four to the batter and
   * must not corrupt strike rotation (PRD §6.4).
   */
  readonly overthrow: boolean;
  /** Short runs are deducted from the total and the ball is flagged. */
  readonly shortRun: number;
}

/**
 * The full dismissal enum, as specified by PRD §6.4.
 *
 * Three entries overlap with another encoding, and the canonical form matters
 * because a rule written against one spelling silently misses the other. The
 * engine treats these as the single source of truth:
 *
 * - **`caught_behind`** is a catch by the keeper — it must also have the keeper
 *   first in `fielderIds`. Any rule about catches (a fielder's catch tally, "no
 *   catch off a no-ball") must match `caught`, `caught_behind` *and*
 *   `caught_and_bowled`. Use `CATCH_DISMISSALS` below rather than listing them.
 * - **`mankad`** is a run out of the non-striker, so `playerOutId` must be the
 *   non-striker. It is distinct from `run_out` only for display.
 * - **`retired_out`** is recorded as `BatterRetired { reason: "out" }`, **never**
 *   as a `Delivery.wicket`. Retiring out happens between deliveries; putting it on
 *   a delivery would additionally credit a ball faced to the batter and a legal
 *   ball to the bowler. It remains in this enum only so a scorecard can render it.
 *
 * `handled_ball` is retained because historic matches were scored under it.
 */
export type DismissalType =
  | "bowled"
  | "caught"
  | "caught_behind"
  | "caught_and_bowled"
  | "lbw"
  | "run_out"
  | "stumped"
  | "hit_wicket"
  | "retired_out"
  | "obstructing_the_field"
  | "hit_ball_twice"
  | "timed_out"
  | "handled_ball"
  | "mankad";

export interface Wicket {
  readonly type: DismissalType;
  readonly playerOutId: PlayerId;
  /** Ordered: for a run out this renders as `run out (Verma/†Khan)`. */
  readonly fielderIds: readonly PlayerId[];
  /** Runs completed before the dismissal. Load-bearing for run outs. */
  readonly runsCompletedBefore: number;
  /**
   * Which end the dismissal happened at. The next ball's strike is computed from
   * where the *surviving* batter finished, which is why this cannot be inferred.
   *
   * Meaningless for a catch in the deep — use `crossed` for that case.
   */
  readonly end: "strikers" | "non_strikers";
  /**
   * Whether the batters crossed before the dismissal completed. `null` when no run
   * was attempted.
   *
   * Required because it is the only fact that decides strike under
   * `RuleSet.newBatterTakesStrikeOnCaught: false` — leagues on the pre-2017 rule.
   * Striker skies it, batters cross, catch taken: the *non-striker* faces next and
   * the new batter goes to the far end. `runsCompletedBefore` is 0 in that case, so
   * it cannot stand in, and without this field the engine assigns the wrong striker
   * and every subsequent ball in the over is credited to the wrong batter. Also
   * load-bearing for a run out where a run was incomplete.
   */
  readonly crossed: boolean | null;
}

/**
 * Every dismissal that is a catch. Rules about catches must match all three, or a
 * `caught_behind` slips past a check written only against `caught`.
 */
export const CATCH_DISMISSALS = ["caught", "caught_behind", "caught_and_bowled"] as const;

/**
 * Where a scoring shot went (PRD SC-6, wagon wheel; ADR-0022). Opt-in, and never blocks the fast
 * path: a ball with no direction is `shot: null` and every figure is unchanged.
 *
 * The position is stored as it physically was on the ground, seen from behind the bowler's arm
 * with the bowler's end at the top. It is never mirrored for a left-hander. Which side of the bat
 * the batter stands is not on the shot: it is on the `BatterIn` that brought him in (ADR-0023),
 * and replay hands it to `wagon.ts`, which turns the two into "cover" or "point". Keeping one copy
 * of the hand means a hand entered wrongly is corrected by rewriting one event, and every shot he
 * played redraws, with no loss of where any ball went.
 */
export interface Shot {
  /**
   * Degrees clockwise from straight down the ground towards the bowler, in [0, 360).
   * 0 is straight, 90 is square on the right of the screen, 180 is straight behind the batter.
   */
  readonly wagonAngle: number;
  /** 0 at the striker, 1 at the rope. Above 1, up to 1.1, only for a six. */
  readonly wagonDistance: number;
  /** Reserved. Always null in this release; shot types are a later, optional step. */
  readonly shotType: string | null;
}

// ── Who is batting and bowling, and how (ADR-0023) ──────────────────────────

/** Which way a batter stands. Decides which side of the ground is off and which is leg. */
export type BatterHand = "right" | "left";

/** The arm a bowler bowls with. */
export type BowlingArm = "right" | "left";

/**
 * What a bowler delivers, independent of arm. Four, so a scorer at the boundary can tell them
 * apart. "Off-spin" and "leg-spin" are not types: they only describe a right-arm bowler, and
 * the familiar names are built from arm and type together (see `bowlingStyleName`).
 */
export type BowlingType = "fast" | "medium" | "finger_spin" | "wrist_spin";

export const BATTER_HANDS: readonly BatterHand[] = ["right", "left"];
export const BOWLING_ARMS: readonly BowlingArm[] = ["right", "left"];
export const BOWLING_TYPES: readonly BowlingType[] = ["fast", "medium", "finger_spin", "wrist_spin"];

/** Whether a value read back from the ledger or the wire is a hand. Anything else is unknown. */
export const isBatterHand = (value: unknown): value is BatterHand => value === "right" || value === "left";
export const isBowlingArm = (value: unknown): value is BowlingArm => value === "right" || value === "left";
export const isBowlingType = (value: unknown): value is BowlingType =>
  value === "fast" || value === "medium" || value === "finger_spin" || value === "wrist_spin";

/** Opt-in pitch-map capture (PRD §6.2 delivery schema, SC-6). */
export interface Pitch {
  readonly length: "full_toss" | "yorker" | "good" | "short" | "bouncer";
  readonly line: "leg" | "middle" | "off" | "wide_off" | "wide_leg";
  readonly x: number;
  readonly y: number;
}

export interface Delivery extends EventBase {
  readonly type: "Delivery";
  /**
   * False for a wide or no-ball. Only legal deliveries advance the over.
   * Advisory — see the note on `Runs.total`; `replay()` derives this from the
   * extras rather than trusting it.
   */
  readonly legal: boolean;
  /**
   * Advisory, and *not* queryable — these live inside the `payload` jsonb with no
   * column and no index. They exist to make a stored payload legible to a human
   * reading the table, nothing more. `replay()` computes the true over and ball
   * from the effective ledger.
   */
  readonly over: number;
  readonly ballInOver: number;

  /**
   * Who was at each end, as the scorer's device believed at the time.
   *
   * Advisory, for the same reason `Runs.total` is: `replay()` derives the pair from
   * the fold and only falls back to these when it cannot — a delivery with no
   * `BatterIn` ahead of it to say who is in.
   *
   * Strike is the one participant a correction moves retrospectively. Correct seq 43
   * from an even score to an odd one and the twelve deliveries after it were faced by
   * the other batter, whatever these fields say; trusting them would fix the total and
   * leave twelve balls credited to the wrong batting line, which is exactly the
   * internally-inconsistent scorecard ADR-0004 exists to prevent.
   *
   * **Except on a correcting event, where they are authoritative.** A superseding delivery's
   * pair is what a human typed while looking at that ball, not what the app inferred, so it
   * re-anchors the ends at that address and every ball after it re-derives from there — which
   * is the whole of the retrospective strike swap (#15, ADR-0004).
   *
   * They are written on every event regardless, because they are what the device observed
   * and the audit trail is worth more than the bytes.
   */
  readonly strikerId: PlayerId;
  readonly nonStrikerId: PlayerId;
  /**
   * Who bowled it. **Not** advisory, unlike the pair above: who bowled is an
   * independent observation with no dependency on earlier balls, so no correction
   * elsewhere in the innings can change it. Superseding it ball by ball is how an
   * over gets reassigned to a different bowler.
   */
  readonly bowlerId: PlayerId;
  readonly keeperId: PlayerId | null;

  readonly runs: Runs;
  readonly wicket: Wicket | null;
  /** True when this ball *is* a free hit, not when it causes one. */
  readonly freeHit: boolean;

  readonly shot: Shot | null;
  readonly pitch: Pitch | null;
  /** Auto-generated per ball (PRD SC-4), overridable by the scorer (SC-5). */
  readonly commentary: string | null;
}

// ── Non-delivery events ─────────────────────────────────────────────────────

export type InningsEndReason =
  | "all_out"
  | "overs_complete"
  | "target_reached"
  | "declared"
  | "forfeited"
  /**
   * The batting side gave up part-way through — walked off, ran out of players, would
   * not continue.
   *
   * Distinct from `forfeited`, which is declining to bat at all, and both distinct from
   * `abandoned`, which is nobody's fault. `ResultKind` already carries the three
   * separately, and a points table treats them differently, so folding any pair together
   * would misreport a standings position. This is overwhelmingly the grassroots case: a
   * side two players short after the interval.
   */
  | "conceded"
  | "abandoned";

export interface InningsStart extends EventBase {
  readonly type: "InningsStart";
  readonly battingTeamId: TeamId;
  readonly bowlingTeamId: TeamId;
  /** Null in a timed format, which has no over limit. */
  readonly oversAvailable: number | null;
  /** Set for a chase. Null for the first innings of an innings pair. */
  readonly target: number | null;
  /**
   * True for a super-over innings. Without it, innings 5 and 6 are
   * indistinguishable from a normal innings — so the result would report `"runs"`
   * rather than a super-over win, and the batting and bowling cards would pollute
   * career statistics.
   */
  readonly isSuperOver: boolean;
  /** True when the batting side is following on. */
  readonly followOn: boolean;
}

export interface InningsEnd extends EventBase {
  readonly type: "InningsEnd";
  readonly reason: InningsEndReason;
}

export interface BatterIn extends EventBase {
  readonly type: "BatterIn";
  readonly playerId: PlayerId;
  /**
   * Whether the incoming batter is on strike for the next delivery.
   *
   * Expressed this way rather than as an end, because "striker's end" is not a
   * fixed place — it swaps at every end change, so `end: "strikers"` means
   * different things depending on whether this event is folded before or after the
   * over's `NewOver`. That ambiguity breaks exactly the case PRD §6.4 calls out:
   * a wicket on the last ball of the over, where new-batter-then-end-change and
   * end-change-then-new-batter put the new batter on opposite ends. `takesStrike`
   * is also simply what the scorer knows and taps.
   */
  readonly takesStrike: boolean;
  /**
   * Which way he stands for this innings (ADR-0023). Absent on an event written before the
   * hand was recorded, which replays as unknown and never as right-handed.
   *
   * Correcting a wrong hand supersedes this event, so it takes effect from his arrival and
   * every shot he played redraws. The player's profile only supplies what this is pre-filled
   * with the next time; the scorecard and the wheel read the ledger.
   */
  readonly battingHand?: BatterHand;
}

export interface BatterRetired extends EventBase {
  readonly type: "BatterRetired";
  readonly playerId: PlayerId;
  /**
   * `hurt` and `not_out` do not count as a wicket and the batter may return at a
   * fall of wicket — critically, the innings must not end at 10 down because of
   * one. `out` counts as a wicket and the batter cannot return (PRD §6.4).
   */
  readonly reason: "hurt" | "out" | "not_out";
}

export interface BowlerChange extends EventBase {
  readonly type: "BowlerChange";
  readonly bowlerId: PlayerId;
  /** Set when the scorer overrode a rule warning, e.g. consecutive overs. */
  readonly overrideReason: string | null;
  /** The arm he bowls with and what he delivers from here on (ADR-0023). Absent when not recorded. */
  readonly bowlingArm?: BowlingArm;
  readonly bowlingType?: BowlingType;
}

export interface NewOver extends EventBase {
  readonly type: "NewOver";
  readonly overNo: number;
  readonly bowlerId: PlayerId;
  /**
   * Set when the scorer took a bowler the RuleSet warned against — his fifth over, or a
   * second in a row where that is disallowed.
   *
   * PRD §6.4 makes bowler restrictions *warnings the scorer may override*, never blocks:
   * the scorer is the authority and the game does not pause for our opinion. But an
   * override without a reason is indistinguishable from a mis-tap six weeks later, so the
   * reason is the whole point of allowing it.
   *
   * Mirrors the field `BowlerChange` already carries, because the same override has to be
   * recordable however the bowler took the ball — without it, an override at the start of
   * an over had nowhere to be written down.
   */
  readonly overrideReason: string | null;
  /**
   * The arm and the type he bowls this over (ADR-0023). The type may differ from one over to the
   * next; the earlier overs keep theirs. Absent on an event written before they were recorded,
   * which replays as unknown.
   */
  readonly bowlingArm?: BowlingArm;
  readonly bowlingType?: BowlingType;
}

export interface Interruption extends EventBase {
  readonly type: "Interruption";
  readonly reason: "rain" | "bad_light" | "crowd" | "other";
  readonly note: string | null;
}

/**
 * Play restarts after an `Interruption`.
 *
 * Needed because an interruption otherwise never ends: rain stops play, twenty
 * minutes later play resumes with no overs lost, so no `OversRevised` is emitted
 * and the header would show a permanent rain badge for the rest of the match. The
 * only alternative would be voiding the `Interruption`, which per CONTEXT.md means
 * "never happened" — falsifying the record of a real stoppage.
 */
export interface PlayResumed extends EventBase {
  readonly type: "PlayResumed";
  readonly interruptionEventId: EventId;
}

export interface OversRevised extends EventBase {
  readonly type: "OversRevised";
  readonly revisedOvers: number;
  readonly revisedTarget: number | null;
  /**
   * Never `"dls"`. DLS is licensed IP and mislabelling our output is both a legal
   * and a credibility risk (PRD §6.6).
   */
  readonly method: "par_score" | "vjd" | "average_run_rate" | "manual";
}

export interface PenaltyRuns extends EventBase {
  readonly type: "PenaltyRuns";
  readonly runs: number;
  /**
   * Which side receives the runs, relative to *this* innings. `"fielding"` means
   * they land on the fielding side's total — and because that side may have two
   * innings in a four-innings match, `creditToInningsNo` says which.
   */
  readonly awardedTo: "batting" | "fielding";
  /**
   * The innings whose total these runs join. Explicit rather than inferred, so two
   * copies of the engine cannot choose differently.
   */
  readonly creditToInningsNo: InningsNo;
  readonly reason: string | null;
}

export interface DeadBall extends EventBase {
  readonly type: "DeadBall";
  readonly reason: string | null;
}

export interface ScorerHandoff extends EventBase {
  readonly type: "ScorerHandoff";
  readonly fromDeviceId: DeviceId | null;
  readonly toDeviceId: DeviceId;
  readonly toScorerId: UserId;
  /** True when an organiser forced a takeover from a device holding unsynced
   *  events, which requires human reconciliation afterwards (PRD §7.2). */
  readonly forced: boolean;
}

/**
 * An amendment that cannot be expressed as a superseding event.
 *
 * Almost every correction is just a new version of one event carrying
 * `supersedes` — including reassigning an over, which the client emits as one
 * superseding Delivery per ball in that over. Keeping a single mechanism is worth
 * more than saving rows. This type exists for the one flow that genuinely spans
 * events: "this was actually a different player", applied across the innings from
 * a chosen point (PRD §6.7).
 */
export interface Correction extends EventBase {
  readonly type: "Correction";
  readonly reason: string;
  readonly actorId: UserId;
  readonly substitution: {
    readonly fromPlayerId: PlayerId;
    readonly toPlayerId: PlayerId;
    readonly fromSeq: number;
  } | null;
}

export interface MatchCompleted extends EventBase {
  readonly type: "MatchCompleted";
  readonly resultText: string;
  readonly winnerTeamId: TeamId | null;
}

/**
 * Leaving `completed` is a ceremony with a mandatory reason, because a published
 * result anyone could quietly change invites the exact accusation the correction
 * feature exists to defuse (ADR-0005).
 */
export interface MatchReopened extends EventBase {
  readonly type: "MatchReopened";
  readonly reason: string;
  readonly actorId: UserId;
}

// ── The union ───────────────────────────────────────────────────────────────

export type NonDeliveryEvent =
  | InningsStart
  | InningsEnd
  | BatterIn
  | BatterRetired
  | BowlerChange
  | NewOver
  | Interruption
  | PlayResumed
  | OversRevised
  | PenaltyRuns
  | DeadBall
  | ScorerHandoff
  | Correction
  | MatchCompleted
  | MatchReopened;

export type Event = Delivery | NonDeliveryEvent;

export type EventType = Event["type"];

/** Narrowing helper, so consumers don't hand-write the discriminant check. */
export const isDelivery = (event: Event): event is Delivery => event.type === "Delivery";
